import React, { useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

/** Redux **/
import { useDispatch } from 'react-redux'
import { loadingActions } from '../../store/loading'
import { authActions } from '../../store/signIn'

/** Material UI **/
import {
  ChevronLeft as ChevronLeftIcon,
  PersonOutline as PersonOutlineIcon
} from '@mui/icons-material'
import { HighlightButton } from '../../styles/mui_custom_components'
import { Box, Grid, InputAdornment, TextField, Typography } from '@mui/material'
import { forgotPasswordStyles } from '../../styles/classes/LogInClasses'

/** Validations **/
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validateEmail } from '../../lib/Global'

/** Components **/
import { requestResetPassword } from '../../services/AuthService'
import ReactGA from 'react-ga4'
import LogInContainer from '../../components/LogInContainer'

/** Images **/
import { images } from '../../lib/Constants'

const ForgotPassword = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const { classes } = forgotPasswordStyles()
  const dispatch = useDispatch()
  const [email, setEmail] = useState('')
  const [errorApi, setErrorApi] = useState('')

  /** VALIDATIONS **/
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .required(t('general.messages.errors.required'))
      .email(t('general.messages.errors.email'))
  })

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema)
  })

  const onSubmit = async data => {
    dispatch(loadingActions.show())
    try {
      const response = await requestResetPassword(email)
      if (response && response.status) {
        dispatch(loadingActions.hide())
      } else {
        ReactGA.event({
          category: 'request',
          action: 'forgot_password_request'
        })
        dispatch(authActions.setChangedEmail(email))
        dispatch(loadingActions.hide())
        history.replace('/forgot-password/sent')
      }
    } catch (error) {
      dispatch(loadingActions.hide())
      if (error.code >= 400 && error.code <= 599) {
        setErrorApi(t('forgot_password.email_not_found'))
      }
      console.error(error)
    }
  }

  const handleEmailChange = event => {
    const value = event.target.value.replace(/\s/g, '')
    setEmail(value)
    setErrorApi('')
  }

  return (
    <LogInContainer>
      <img alt={'mywork Logo'}
        className={classes.myworkLogo}
        src={images.logo} />
      <Grid
        data-testid={'forgot_password_page'}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        className={classes.mainGrid}
        mt={3}
      >
        <Grid className={classes.mainItem} item xs={12}>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12} textAlign="center">
              <Typography className={classes.title}>
                {t('forgot_password.title').toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Typography align="center" className={classes.subtitle}>
                {t('forgot_password.subtitle_1')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box mt={4}>
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <Box justifyContent={'center'} display={'flex'}>
                    <Box className={classes.formElementsContainer}>
                      <TextField
                        className={classes.emailField}
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        placeholder={t('forgot_password.email')}
                        name="email"
                        autoComplete="off"
                        autoFocus
                        error={!!errors.email || !!errorApi}
                        helperText={
                          (errors.email && errors.email.message) || errorApi
                        }
                        {...register('email')}
                        FormHelperTextProps={{
                          classes: {
                            root: classes.errorText,
                            error: classes.errorText
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon />
                            </InputAdornment>
                          ),
                          classes: {
                            notchedOutline: classes.fieldsOutlined
                          }
                        }}
                        onInput={handleEmailChange}
                        value={email}
                      />
                      <Box className={classes.buttonsBox}>
                        <Box className={classes.backToSignInBox}>
                          <Box>
                            <ChevronLeftIcon />
                          </Box>
                          <Box>
                            <Link
                              data-testid={'back_log_in'}
                              className={classes.link}
                              to="/sign-in"
                              variant="body2"
                            >
                              {t('forgot_password.back_to_log_in')}
                            </Link>
                          </Box>
                        </Box>

                        <Box className={classes.sendButtonBox}>
                          <HighlightButton
                            data-testid={'submit_button'}
                            disabled={!validateEmail(email)}
                            className={classes.sendButton}
                            type="submit"
                            variant="contained"
                          >
                            {t('forgot_password.send')}
                          </HighlightButton>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </LogInContainer>
  )
}

export default ForgotPassword
